import React, { useRef } from 'react';

import { useStableCalendarOverlay } from 'hooks';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';

import { selectedDate } from '../../types/interfaces';
import { formatDateToYYYYMMDD, parseYYYYMMDD } from '../../utils/date-utils';

type CalendarOnChangeEvent = Parameters<
  NonNullable<React.ComponentProps<typeof Calendar>['onChange']>
>[0];

export type NewDatepickerProps = {
  customWidth?: string | null;
  treeDropdownPlaceholder?: string;
  datepickerDate?: selectedDate;
  onChange?: any;
  errors?: any;
  tooltip?: {
    left?: string;
    right?: string;
  };
  gap?: string;
  useDateOnlyFormat?: boolean;
};

const NewDatepicker: React.FC<NewDatepickerProps> = ({
  customWidth,
  onChange,
  datepickerDate,
  errors,
  tooltip,
  gap,
  useDateOnlyFormat = false
}) => {
  const toast = useRef<Toast>(null);
  const dateFromOverlay = useStableCalendarOverlay();
  const dateUntilOverlay = useStableCalendarOverlay();

  const formatDateLocal = formatDateToYYYYMMDD;
  const getLocalDateFromString = parseYYYYMMDD;

  const handleDateFromChange = (e: CalendarOnChangeEvent) => {
    const newDate = e.value as Date | null;
    if (!newDate) {
      onChange?.({ ...datepickerDate, dateFrom: '' });
      return;
    }

    if (useDateOnlyFormat) {
      onChange?.({ ...datepickerDate, dateFrom: formatDateLocal(newDate) });
    } else {
      onChange?.({ ...datepickerDate, dateFrom: newDate.toISOString() });
    }
  };

  const handleDateUntilChange = (e: CalendarOnChangeEvent) => {
    const newDate = e.value as Date | null;
    if (!newDate) {
      onChange?.({ ...datepickerDate, dateUntil: '' });
      return;
    }

    if (useDateOnlyFormat) {
      onChange?.({ ...datepickerDate, dateUntil: formatDateLocal(newDate) });
    } else {
      const endOfDay = new Date(newDate);
      endOfDay.setHours(23, 59, 59, 999);
      onChange?.({ ...datepickerDate, dateUntil: endOfDay.toISOString() });
    }
  };

  const parseDate = (raw?: string): Date | null => {
    if (!raw) return null;
    return useDateOnlyFormat ? getLocalDateFromString(raw) : new Date(raw);
  };

  return (
    <div className="flex flex-column align-items-center datepicker-container">
      <Toast ref={toast} />
      <div className={`${gap || ''} flex`}>
        <div className="flex-column justify-content-center">
          <Calendar
            {...dateFromOverlay}
            placeholder="С даты"
            inputId="dateFrom"
            value={parseDate(datepickerDate?.dateFrom)}
            onChange={handleDateFromChange}
            showButtonBar
            style={{ width: customWidth ?? '' }}
            tooltip={
              errors?.generalDatesErrors?.startDate.message ||
              errors?.overlappingError?.message ||
              tooltip?.left
            }
            className={
              errors?.generalDatesErrors?.startDate || errors?.overlappingError
                ? 'p-invalid'
                : ''
            }
          />
          <p
            className={`asset-type-card-errors card-errors ${
              errors?.startDate ? 'text-red-500' : 'text-transparent'
            }`}
          >
            {errors?.startDate?.message || ' '}
          </p>
        </div>
        <div className="flex-column">
          <Calendar
            {...dateUntilOverlay}
            placeholder="До даты"
            inputId="dateUntil"
            value={parseDate(datepickerDate?.dateUntil)}
            onChange={handleDateUntilChange}
            showButtonBar
            style={{ width: customWidth ?? '' }}
            tooltip={
              errors?.generalDatesErrors?.endDate?.message ||
              errors?.overlappingError?.message ||
              tooltip?.right
            }
            className={
              errors?.generalDatesErrors?.endDate || errors?.overlappingError
                ? 'p-invalid'
                : ''
            }
          />
          <p
            className={`asset-type-card-errors card-errors ${
              errors?.endDate ? 'text-red-500' : 'text-transparent'
            }`}
          >
            {errors?.endDate?.message || ' '}
          </p>
        </div>
      </div>
    </div>
  );
};

export default NewDatepicker;
